import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppIcon, Coach, PageHeader } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CalendarBookingModal } from '@backrs_ui/grow-ui/src/components/CalendarBookingModal/CalendarBookingModal';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  CONSULTATION_PRODUCT_CODE,
  useGetProductByCodeQuery,
} from '@backrs_ui/grow-ui/src/redux/products/products.api';
import { selectUserId } from '@backrs_ui/grow-ui/src/redux/user/user';
import { CAL_TEAM_CONSULTATION_URL } from '@backrs_ui/grow-ui/src/settings';

import { Box, Button, SxProps, Theme } from '@mui/material';

import { CoachesCategoryFilter, CategoryTab } from '../molecules/CoachesCategoryFilter';
import { CoachesContent } from '../molecules/CoachesContent';

const STRINGS = appStrings.modules.coaches;
const DEEP_DIVE_STRINGS = appStrings.modules.deepDives;

// Use deep dive category names as filter values (skip "Most Popular" at index 0)
export const COACH_CATEGORY_TABS: CategoryTab[] = [
  { value: 'all', label: STRINGS.tabs.all },
  ...DEEP_DIVE_STRINGS.categories.slice(1).map((categoryName) => ({
    value: categoryName,
    label: categoryName,
  })),
];

type CoachesPageProps = Readonly<{
  coaches: Coach[];
  deepDiveCategoryMap: Map<number, string>;
}>;

export const CoachesPage = ({ coaches, deepDiveCategoryMap }: CoachesPageProps) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState<Coach | null>(null);

  const navigate = useNavigate();
  const userId = useAppSelector(selectUserId);
  const { data: consultationProduct } = useGetProductByCodeQuery(CONSULTATION_PRODUCT_CODE);

  const availableCategories = useMemo(() => {
    const categoriesWithCoaches = new Set<string>();

    coaches.forEach((coach) => {
      coach.deep_dives?.forEach((dd) => {
        const categoryName = deepDiveCategoryMap.get(dd.id);

        if (categoryName) {
          categoriesWithCoaches.add(categoryName);
        }
      });
    });

    return COACH_CATEGORY_TABS.filter(
      (tab) => tab.value === 'all' || categoriesWithCoaches.has(tab.value),
    );
  }, [coaches, deepDiveCategoryMap]);

  const filteredCoaches = useMemo(() => {
    return coaches.filter((coach) => {
      const matchesCategory =
        selectedCategory === 'all' ||
        coach.deep_dives?.some((dd) => {
          const categoryName = deepDiveCategoryMap.get(dd.id);

          return categoryName === selectedCategory;
        });

      return matchesCategory;
    });
  }, [coaches, selectedCategory, deepDiveCategoryMap]);

  const handleCoachSelect = (coach: Coach) => {
    navigate(`/profile/${coach.id}`);
  };

  const handleScheduleConsultation = () => {
    setSelectedCoach(null);
    setIsBookingModalOpen(true);
  };

  const handleCoachCtaClick = (coach: Coach) => {
    if (coach.consultation_calendar_url) {
      setSelectedCoach(coach);
      setIsBookingModalOpen(true);
    } else {
      // Fall back to team consultation if coach has no calendar
      setSelectedCoach(null);
      setIsBookingModalOpen(true);
    }
  };

  const handleBookingClose = () => {
    setIsBookingModalOpen(false);
    setSelectedCoach(null);
  };

  return (
    <Box sx={containerSx}>
      <Box sx={contentContainerSx}>
        <PageHeader
          title={STRINGS.header.title}
          subtitle={STRINGS.header.subtitle}
          maxWidth={null}
          showBorder={false}
          rightComponents={[
            <Button
              key="explore-deep-dives"
              id="coaches-explore-deep-dives-button"
              hierarchy="Tertiary color"
              size="md"
              onClick={() => navigate(APP_ROUTE.DEEP_DIVES)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {STRINGS.header.exploreDeepDives}
            </Button>,
            <CoachesCategoryFilter
              key="filter"
              categories={availableCategories}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
            />,
            <Button
              key="consultation"
              id="coaches-schedule-consultation-button"
              hierarchy="Primary"
              size="md"
              startIcon={<AppIcon iconName="CalendarPlus01" diameter="20px" color="currentColor" />}
              onClick={handleScheduleConsultation}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {STRINGS.header.scheduleConsultation}
            </Button>,
          ]}
        />

        <CoachesContent
          coaches={filteredCoaches}
          onCardClick={handleCoachSelect}
          onCtaClick={handleCoachCtaClick}
        />
      </Box>

      {isBookingModalOpen && (selectedCoach || consultationProduct) && (
        <CalendarBookingModal
          open={isBookingModalOpen}
          userId={userId}
          productId={selectedCoach?.consultation_product_id ?? consultationProduct?.id ?? 0}
          coachProductId={selectedCoach?.consultation_coach_product_id}
          calendarUrl={selectedCoach?.consultation_calendar_url ?? CAL_TEAM_CONSULTATION_URL}
          bookingContext="Performance Coaching"
          onClose={handleBookingClose}
        />
      )}
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  containerType: 'inline-size',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  margin: '0 auto',
};

const contentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  alignSelf: 'center',
  gap: backrs.spacing['spacing-2xl'],
});
